import type { NativeEmbedClientId } from '../../common/types/experience';
import type { Surface } from '../../common/types/surface/types';
import type { NativeEmbedHostClient } from '../client';

export type { Surface };

let surfaceServiceInstance: SurfaceService | undefined;

/**
 * Returns the singleton {@link SurfaceService} instance.
 */
export function getSurfaceService(): SurfaceService {
	if (!surfaceServiceInstance) {
		surfaceServiceInstance = new SurfaceService();
	}
	return surfaceServiceInstance;
}

/**
 * Service that owns the host surface's contract with the native-embeds system.
 *
 * A surface encapsulates capabilities that differ per host (editor, renderer,
 * other products). Today the only capability is scroll handling; the interface
 * will grow as new surface concerns are identified.
 *
 * Scroll handling: when a child embed with `disable-child-scroll` sends a
 * scroll event, `SurfaceService` validates that the sender is a registered
 * experience (via the supplied callback) and forwards the event to the host's
 * scroll handler. If no handler is registered, events are silently dropped.
 *
 * - Call {@link setSurface} to register or replace the active surface.
 * - Pass `null` to clear the surface (e.g. on unmount).
 * - Last-write-wins: calling `setSurface` again replaces the previous surface.
 */
export class SurfaceService {
	private surface: Surface | null = null;

	/**
	 * Register or replace the active host surface.
	 *
	 * @param surface - The surface configuration, or `null` to clear.
	 */
	setSurface(surface: Surface | null): void {
		this.surface = surface;
	}

	/**
	 * Subscribe to scroll events from the bridge client, forwarding them to the
	 * host surface's scroll handler when the sender is a registered experience.
	 *
	 * @param client - The bridge client to listen on.
	 * @param hasExperience - Callback that returns `true` if the given sender ID
	 *   belongs to a registered experience.
	 */
	registerScrollListener(
		client: NativeEmbedHostClient,
		hasExperience: (senderId: NativeEmbedClientId) => boolean,
	): void {
		client.onScrollEvent(({ senderId, deltaX, deltaY }) => {
			const handler = this.surface?.scrollHandler;
			if (hasExperience(senderId) && handler) {
				handler({ deltaX, deltaY });
			}
		});
	}
}
